/**
 * Sentry initialization for Vite + React frontend applications
 *
 * @example
 * ```typescript
 * // src/sentry.ts
 * import { initSentryVite } from '@makanly/sentry/frontend';
 *
 * initSentryVite({
 *   dsn: import.meta.env.VITE_SENTRY_DSN,
 *   environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
 * });
 *
 * // main.tsx (import at the top, before React render)
 * import './sentry';
 * ```
 */
import * as Sentry from '@sentry/react';
/**
 * Initialize Sentry for Vite + React applications
 *
 * Note: Vite environment variables should be passed directly from the app,
 * not read from this library, since import.meta.env is not available at
 * library build time.
 */
export function initSentryVite(config) {
    const dsn = config.dsn;
    if (!dsn) {
        console.log('[Sentry] No DSN provided, skipping initialization');
        return;
    }
    const appName = config.app;
    const environment = config.environment || 'development';
    const release = config.release;
    const tracesSampleRate = config.tracesSampleRate ?? 0.1;
    const replaysSessionSampleRate = config.replaysSessionSampleRate ?? 0.01;
    const replaysOnErrorSampleRate = config.replaysOnErrorSampleRate ?? 1.0;
    const debug = config.debug ?? false;
    const tracePropagationTargets = config.tracePropagationTargets || [
        'localhost',
        /^https:\/\/.*\.makanly\.com\.my/,
        /^https:\/\/api\.makanly\.com\.my/,
    ];
    Sentry.init({
        dsn,
        environment,
        release,
        debug,
        // App identification
        initialScope: {
            tags: {
                app: appName,
            },
        },
        // Performance monitoring
        tracesSampleRate,
        // Session Replay
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: true,
            }),
            Sentry.browserTracingIntegration(),
        ],
        // Trace propagation targets
        tracePropagationTargets,
        // Filter out spurious errors
        beforeSend(event) {
            const errorValue = event.exception?.values?.[0]?.value;
            if (errorValue?.includes('AbortError') || errorValue?.includes('cancelled')) {
                return null;
            }
            return event;
        },
    });
    console.log(`[Sentry] Vite app (${appName}) initialized in ${environment} environment`);
}
/**
 * Set user context for Sentry (call after authentication)
 */
export function setSentryUser(user) {
    Sentry.setUser({
        id: user.id,
        email: user.email,
    });
    if (user.role) {
        Sentry.setTag('user.role', user.role);
    }
}
/**
 * Set tenant context for Sentry
 */
export function setSentryTenant(tenant) {
    Sentry.setTags({
        'tenant.restaurant_id': tenant.restaurantId || 'unknown',
        'tenant.outlet_id': tenant.outletId || 'unknown',
    });
    Sentry.setContext('tenant', tenant);
}
/**
 * Clear user context (call on logout)
 */
export function clearSentryUser() {
    Sentry.setUser(null);
}
/**
 * Re-export Sentry for direct access
 */
export { Sentry };
//# sourceMappingURL=vite.js.map